import type { TabContextProps } from '@mui/lab/TabContext';

import { createContextFactory } from '@mfw/components';
import { useSearchParams } from 'react-router-dom';
import MuiTabContext from '@mui/lab/TabContext';
import React from 'react';

export interface TabsContextProps extends Omit<TabContextProps, 'value'> {
  initialValue: TabContextProps['value'];
  queryParamKey?: string;
  /**
   * The tab values this strip actually has, when it wants the URL vetted.
   *
   * A `queryParamKey` value wins over `initialValue` unconditionally, and every
   * `TabsPanel` renders `null` unless it matches — so a `?tab=` that names no
   * panel (a hand-edited URL, a link that outlived a renamed tab) leaves the
   * page with its tab strip and nothing under it. Listed here, an unknown value
   * is ignored instead and the strip opens on `initialValue`.
   *
   * Omitted: any value is taken, which is the behaviour every existing caller
   * already has.
   *
   * STRINGS only, deliberately. A query param is a string and `MuiTabContext`
   * takes a string, so a numeric entry could only ever be matched by coercing
   * it — and the value that then reaches `TabsPanel` is still the URL's string,
   * which its strict `!==` would reject against a numeric `value` prop. The
   * strip would vet the URL, accept it, and render no panel at all: the exact
   * blank page this prop exists to prevent. The type is the guard.
   */
  allowedValues?: string[];
}

type TabsContextType = {
  setCurrentTabValue: React.Dispatch<React.SetStateAction<number | string>>;
  value: number | string;
  queryParamKey?: string;
};
const TabsContext = createContextFactory({
  devToolsName: 'TabsContext',
  initialValue: {} as TabsContextType,
});

const TabsProvider = ({
  allowedValues,
  children,
  initialValue,
  queryParamKey,
}: TabsContextProps) => {
  // ONE reader for the URL, on purpose. The value the strip starts on used to
  // come from `useQueryParams().get` while the value it tracks came from here —
  // two readers of the same query string, the first a wrapper over the second
  // that additionally runs `decodeURIComponent` on a value `URLSearchParams`
  // has ALREADY decoded. `?tab=100%` is a legal URL that survives the first
  // decode as the literal `100%` and then throws `URIError` in the second, from
  // inside a `useState` initializer — i.e. one mistyped character in the
  // address bar white-screened every page that names a tab param. It also
  // dragged the whole `router/hooks` barrel (→ `src/config` → the
  // rspack-injected `API_BASE_URL`) into a component that needed none of it.
  const [search, setSearch] = useSearchParams();

  // `?tab=` with nothing after it reads as `''`, which `??` below would keep —
  // so the empty case is rejected here rather than left to the caller's list.
  const takeUrlValue = (value?: null | string) =>
    value && (!allowedValues || allowedValues.includes(value)) ? value : null;

  const urlValue = queryParamKey
    ? takeUrlValue(search.get(queryParamKey))
    : null;

  const [internalValue, setInternalValue] = React.useState(
    urlValue ?? initialValue,
  );

  // A key that is PRESENT but names no panel is a statement — "no tab chosen" —
  // and the answer to it is `initialValue`, which is what this prop's contract
  // promises. Falling through to `internalValue` instead made the reset last
  // only until the first tab click: after that the strip kept whatever was open,
  // so Back onto such a URL moved the address bar and left the page exactly
  // where it was.
  //
  // A key that is ABSENT says nothing about the tab, so the strip keeps what it
  // has — that is every navigation that simply does not carry the param.
  const hasUnusableUrlValue = Boolean(
    queryParamKey && !urlValue && search.has(queryParamKey),
  );
  const currentTabValue =
    urlValue ?? (hasUnusableUrlValue ? initialValue : internalValue);

  // Keep the remembered tab in step with the one ON SCREEN. Both URL-driven
  // outcomes above bypass `setInternalValue` (only a tab click writes it), so
  // after a link with a value, or after an unusable value forced
  // `initialValue`, the memory held a tab the strip was no longer showing —
  // and the next navigation that merely dropped the key fell through to it,
  // switching the page with no tab interaction at all. A render-phase state
  // adjustment on purpose (React's sanctioned derived-state form): an effect
  // would commit one frame in which "keeps what it has" reads the stale value.
  if (internalValue !== currentTabValue) {
    setInternalValue(currentTabValue);
  }

  const valueRef = React.useRef(currentTabValue);
  valueRef.current = currentTabValue;

  const setCurrentTabValue = React.useCallback<
    React.Dispatch<React.SetStateAction<number | string>>
  >(
    (action) => {
      const next =
        typeof action === 'function' ? action(valueRef.current) : action;
      setInternalValue(next);
      if (queryParamKey) {
        // tab click = navigation → PUSH (back/forward walks through tabs —
        // owner decision, R-AUD-72); the global useQueryParams.set stays
        // replace for non-navigational URL writes
        setSearch(
          (previous) => {
            const nextSearch = new URLSearchParams(previous.toString());
            nextSearch.set(queryParamKey, String(next));
            return nextSearch;
          },
          { replace: false },
        );
      }
    },
    [queryParamKey, setSearch],
  );

  const providedValue = React.useMemo(
    () => ({ setCurrentTabValue, value: currentTabValue }),
    [currentTabValue, setCurrentTabValue],
  );

  return (
    <TabsContext.Provider value={providedValue}>
      <MuiTabContext value={currentTabValue}>{children}</MuiTabContext>
    </TabsContext.Provider>
  );
};

export default TabsProvider;

export function useTabsContext() {
  return TabsContext.useContext();
}
